import { usePropertiesConfigContext } from "components/context/propertiesConfig/customProperties";
import { useMemo } from "react";
import { CurrencyRowCellRenderer } from "./currencyRowCellRenderer";
import { getCurrencySymbol } from "components/custom/getCurrencySymbol";

const formatNumber = (
  amount,
  currencyFormat,
  amountSymbol,
  decimalCount = 0,
) => {
  const formattedAmount = new Intl.NumberFormat(currencyFormat, {
    minimumFractionDigits: decimalCount,
  }).format(amount);

  return `${amountSymbol} ${formattedAmount}`;
};

export const FooterCell = ({
  rows,
  column: {
    id: columnName,
    isDisplayTotal = false,
    totalDecimalCount,
    isSelectedTotal = false,
    setFooterValue,
    footerLabel = "",
    footerIsMultivalue = false,
    currencyRefColumn,
    isCurrencyCode,
    Cell,
  },
}) => {
  const { currencyFormat, decimalCount, dynamicAmountSymbol } =
    usePropertiesConfigContext();
  let lsLabel = footerLabel;

  let newrowdata = rows.filter((item) => !item.original.ignoreValue);
  const total = useMemo(
    () =>
      newrowdata.reduce(
        (sum, row) => Number(row.values?.[columnName] ?? 0) + sum,
        0,
      ),
    [newrowdata, columnName],
  );
  // Filter rows where isSelected is true
  const selectData = rows.filter((row) => row.isSelected);
  // Compute total for selected rows
  const totalForSelected = useMemo(
    () =>
      selectData.reduce(
        (sum, row) => Number(row.values?.[columnName] ?? 0) + sum,
        0,
      ),
    [selectData, columnName],
  );

  const amountSymbol = !isCurrencyCode
    ? getCurrencySymbol(currencyRefColumn ?? dynamicAmountSymbol)
    : currencyRefColumn ?? dynamicAmountSymbol;

  const formattedTotal = useMemo(() => {
    if (!setFooterValue) {
      return isNaN(total)
        ? ""
        : Cell === CurrencyRowCellRenderer
          ? formatNumber(
              total,
              currencyFormat,
              amountSymbol,
              totalDecimalCount ?? decimalCount,
            )
          : total;
    }
    if (typeof setFooterValue === "function") {
      const resp = setFooterValue(total, newrowdata);
      if (footerIsMultivalue) {
        if (Array.isArray(resp)) {
          resp?.forEach((value, index) => {
            lsLabel = lsLabel.replace(`{${index}}`, value);
          });
        }
        return lsLabel;
      }
      return isNaN(resp) ? 0 : resp;
    }
  }, [selectData, totalForSelected, total, newrowdata]);

  const formattedTotalForSelected = isNaN(totalForSelected)
    ? ""
    : Cell === CurrencyRowCellRenderer
      ? formatNumber(
          totalForSelected,
          currencyFormat,
          amountSymbol,
          totalDecimalCount ?? decimalCount,
        )
      : totalForSelected;

  // Return both totals based on conditions
  return (
    <>
      {isDisplayTotal && formattedTotal !== "" && (
        <span
          style={{
            textOverflow: "ellipsis",
            overflow: "hidden",
            whiteSpace: lsLabel.includes("\n") ? "pre-line" : "nowrap",
            textAlign: "center",
          }}
        >
          {`${Boolean(lsLabel) ? lsLabel : ""}`}{" "}
          {!footerIsMultivalue && formattedTotal}
        </span>
      )}

      {isSelectedTotal && formattedTotalForSelected !== "" && (
        <span style={{ paddingLeft: "10px" }}>
          Selected Total: {formattedTotalForSelected}
        </span>
      )}
    </>
  );
};
